import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Badge } from 'react-bootstrap';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../../utils/api';

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
    ['link', 'image'],
    ['clean']
  ],
};

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image'
];

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    isPublished: true,
    image: null,
  });
  const [editingId, setEditingId] = useState(null);

  const fetchPosts = async () => {
    try {
      const { data } = await api.get('/blog/admin');
      setPosts(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleClose = () => {
    setShow(false);
    setEditingId(null);
    setFormData({
      title: '',
      summary: '',
      content: '',
      isPublished: true,
      image: null,
    });
  };

  const handleShow = (post = null) => {
    if (post) {
      setEditingId(post._id);
      setFormData({
        title: post.title,
        summary: post.summary,
        content: post.content,
        isPublished: post.isPublished,
        image: null,
      });
    }
    setShow(true);
  };

  const handleChange = (e) => {
    if (e.target.name === 'image') {
      setFormData({ ...formData, image: e.target.files[0] });
    } else if (e.target.name === 'isPublished') {
      setFormData({ ...formData, isPublished: e.target.checked });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleEditorChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) data.append(key, formData[key]);
    });

    try {
      if (editingId) {
        await api.put(`/blog/${editingId}`, data);
      } else {
        await api.post('/blog', data);
      }
      fetchPosts();
      handleClose();
    } catch (error) {
      console.error(error);
      alert('Error saving post');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await api.delete(`/blog/${id}`);
        fetchPosts();
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Blog Posts</h3>
        <Button onClick={() => handleShow()}>Add New Post</Button>
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((post) => (
            <tr key={post._id}>
              <td>{post.title}</td>
              <td>
                <Badge bg={post.isPublished ? 'success' : 'warning'}>
                  {post.isPublished ? 'Published' : 'Draft'}
                </Badge>
              </td>
              <td>{new Date(post.createdAt).toLocaleDateString()}</td>
              <td>
                <Button variant="info" size="sm" className="me-2" onClick={() => handleShow(post)}>Edit</Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(post._id)}>Delete</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Post' : 'Add Post'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control name="title" value={formData.title} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Summary (Short description)</Form.Label>
              <Form.Control as="textarea" rows={2} name="summary" value={formData.summary} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <ReactQuill 
                theme="snow" 
                value={formData.content} 
                onChange={handleEditorChange}
                modules={modules}
                formats={formats}
                style={{ height: '300px', marginBottom: '50px' }} 
              />
            </Form.Group>
            <div className="mb-5"></div>
            <Form.Check 
              type="checkbox" 
              label="Published" 
              name="isPublished" 
              checked={formData.isPublished} 
              onChange={handleChange} 
              className="mb-3"
            />
            <Form.Group className="mb-3">
              <Form.Label>Featured Image (Optional)</Form.Label>
              <Form.Control type="file" name="image" onChange={handleChange} />
            </Form.Group>
            <Button variant="primary" type="submit">Save Changes</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BlogManager;